import { z } from "zod";
import { NullishDateTimeWithOffset } from "@/lib/api/schemas/patterns";
import { FileTypes } from "@/lib/api/schemas/shared";

/**
 * Schemas for the next-gen cycle result shape exposed on `CycleOut.steps` (when
 * `processing_type === "nextgen"`). The backend pre-groups the run into one top-level
 * `step` per scenario step (`step.data` is the JSON-encoded instruction string, or
 * `"pre"`/`"post"` for setup/teardown), each carrying `substeps[].events[]` — a
 * timestamp-ordered mix of `message` events (the agent log payloads) and `file` events
 * (per-step screenshots and the run recording).
 */

export const ProcessingTypeSchema = z.enum(["api", "classic", "nextgen"]).catch("classic");
export type ProcessingType = z.infer<typeof ProcessingTypeSchema>;

const ResultEventSchema = z.object({
  type: z.enum(["message", "file"]).nullish(),
  id: z.number().nullish(),
  created_at: NullishDateTimeWithOffset,
  timestamp: NullishDateTimeWithOffset,
  time_since_scheduled_start: z.number().nullish(),
  level: z.string().nullish(),
  message_type: z.string().nullish(),
  // For message events this is the log payload (object, occasionally a JSON string);
  // for file events it is null. Kept raw and normalized in the view transformer.
  data: z.unknown().nullish(),
  // The worker's original `step:message-add` request, verbatim ({turn, type, level,
  // payload, step_id, timestamp}); `{}` on cycles recorded before the backend stored it.
  raw_data: z.unknown().nullish(),
  category: z.string().nullish(),
  file_size: z.number().nullish(),
  url: z.string().nullish(),
  extra_data: z.unknown().nullish(),
});
export type ResultEvent = z.infer<typeof ResultEventSchema>;

// A turn. The backend renamed `substeps`→`turns` and the inner `step`→`turn`; the OpenAPI
// schema still says `substeps`/`step`, so we accept both spellings and normalize to `turn`.
const NextgenTurnSchema = z
  .object({
    turn: z.string().nullish(),
    step: z.string().nullish(),
    events: z.array(ResultEventSchema).default([]),
  })
  .transform((value) => ({ turn: value.turn ?? value.step ?? undefined, events: value.events }));

const NextgenStepSchema = z
  .object({
    id: z.number(),
    created_at: NullishDateTimeWithOffset,
    timestamp: NullishDateTimeWithOffset,
    // JSON-encoded string, e.g. `"\"Zaloguj się…\""` or `"\"pre\""`.
    data: z.string().nullish(),
    files: z
      .array(
        z.object({
          id: z.number(),
          created_at: NullishDateTimeWithOffset,
          category: FileTypes,
          filename: z.string(),
          public_url: z.string().nullable(),
        }),
      )
      .nullish(),
    // `turns` is the current field name; `substeps` is the legacy one — accept either.
    turns: z.array(NextgenTurnSchema).nullish(),
    substeps: z.array(NextgenTurnSchema).nullish(),
  })
  .transform((value) => ({
    id: value.id,
    created_at: value.created_at,
    timestamp: value.timestamp,
    data: value.data,
    files: value.files,
    turns: value.turns ?? value.substeps ?? [],
  }));
export type NextgenStep = z.infer<typeof NextgenStepSchema>;

// Tolerant: skip any malformed step instead of failing the whole cycle parse.
export const NextgenStepsSchema = z.array(z.unknown()).transform((steps) =>
  steps.flatMap((step) => {
    const parsed = NextgenStepSchema.safeParse(step);
    return parsed.success ? [parsed.data] : [];
  }),
);
